import { DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import { ChangeDetectionStrategy, Component, ElementRef, inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ArcadeIframeService } from '@app/services/arcade-iframe.service';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { ButtonComponent } from '@sb/ui/button';
import { SbDialogModule } from '@sb/ui/dialog';
import { LoadingStateComponent } from '@sb/ui/feedback';
import { ToastService } from '@sb/ui/toast';

export interface ArcadeDemoDialogData {
  demoId: string;
}

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'arcade-demo-dialog',
  imports: [SbDialogModule, ButtonComponent, LoadingStateComponent, TranslatePipe],
  templateUrl: './arcade-demo-dialog.component.html',
})
export class ArcadeDemoDialogComponent implements OnInit, OnDestroy {
  data: ArcadeDemoDialogData = inject(DIALOG_DATA);
  private arcadeIframeService: ArcadeIframeService = inject(ArcadeIframeService);
  private translate: TranslateService = inject(TranslateService);
  private dialogRef: DialogRef<boolean> = inject(DialogRef);
  private toast: ToastService = inject(ToastService);
  loading: boolean;
  lastStepReached = false;
  private bailTimeout: ReturnType<typeof setTimeout>;

  @ViewChild('iframeContainer', { static: true })
  iframeContainer: ElementRef;

  private arcadeEventListener: (event: MessageEvent) => void;
  private iframe: HTMLIFrameElement;

  ngOnInit(): void {
    this.loading = true;
    this.iframe = this.arcadeIframeService.createIframe(this.data.demoId);
    this.appendDemoIframe(this.iframeContainer.nativeElement).then((opened) => {
      if (opened) {
        this.loading = false;
      } else {
        this.toast.openDanger(this.translate.instant('Something went wrong trying to start the tour'));
        this.close(false);
      }
    });
  }

  appendDemoIframe(container: HTMLElement): Promise<boolean> {
    return new Promise((resolve) => {
      container.appendChild(this.iframe);

      this.arcadeEventListener = (event) => {
        if (event.origin !== 'https://demo.arcade.software') return;

        switch (event.data.eventName) {
          case 'Flow Rendered':
            clearTimeout(this.bailTimeout);
            resolve(true);
            break;
          case 'Step Reached':
            if (event.data.isFinalStep) {
              this.lastStepReached = true;
            }
            break;
        }
      };
      window.addEventListener('message', this.arcadeEventListener);

      // Ideally we simply fetch the resource and if that would fail (e.g. because of ad blockers), we'd bail.
      // But because of cors issues we sadly can't do that.
      // So instead we listen for an arcade specific event and if that isn't emitted within 10 seconds, we bail.
      this.bailTimeout = setTimeout(() => resolve(false), 10000);
    });
  }

  ngOnDestroy(): void {
    this.cleanUpDemo();
  }

  private cleanUpDemo() {
    if (this.arcadeEventListener) {
      window.removeEventListener('message', this.arcadeEventListener);
    }
    clearTimeout(this.bailTimeout);
    this.iframe?.remove();
  }

  close(demoCompleted: boolean) {
    this.dialogRef.close(demoCompleted);
  }
}
